import {
  GatewayAssujetti,
  GatewayConformite,
  GatewayDeclarationActivite,
} from "@conformite/gateway";
import { SubAxisVerificationsMap } from "@src/data/controle-quinquennal/hook/useCQAxeVerificationsQuery";
import {
  AXE_LABELS,
  getQuestionnaireSlug,
} from "@src/helper/avancement.helper";
import { getScrollParent } from "@src/helper/scroll.helper";
import { getSubAxisForAxe } from "@src/helper/subAxisVerification.helper";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSeedSharedAssujettiAnswers } from "@src/components/questionnaire/useSeedSharedAssujettiAnswers";
import styles from "./AssujettiDetailConformite.module.scss";
import { AxeCard } from "./AxeCard";

type AssujettiDetailConformiteProps = {
  assujetti: GatewayAssujetti.AssujettiWithAvancement;
  availableAxes?: GatewayConformite.AxeConformite[];
  verificationsBySubAxis?: SubAxisVerificationsMap;
  onRelaunchSubAxis?: (subAxis: string) => void;
  relaunchingSubAxis?: string;
  declaredIobspActivites?: GatewayDeclarationActivite.ActiviteIobsp[];
  disabled?: boolean;
};

export function AssujettiDetailConformite({
  assujetti,
  availableAxes,
  verificationsBySubAxis,
  onRelaunchSubAxis,
  relaunchingSubAxis,
  declaredIobspActivites,
  disabled,
}: AssujettiDetailConformiteProps) {
  const axesToDisplay = useMemo(() => {
    return availableAxes?.filter((axe) => AXE_LABELS[axe] !== undefined);
  }, [availableAxes]);

  useSeedSharedAssujettiAnswers(assujetti.id);

  const cardListRef = useRef<HTMLDivElement>(null);
  const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);

  useLayoutEffect(() => {
    const list = cardListRef.current;
    if (!list) return undefined;
    const measure = () => {
      const headers = list.querySelectorAll<HTMLElement>("[data-axe-header]");
      let cumulativeTop = 0;
      const offsets: number[] = [];
      headers.forEach((header) => {
        offsets.push(cumulativeTop);
        cumulativeTop += Math.floor(header.getBoundingClientRect().height);
      });
      setStickyOffsets(offsets);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [axesToDisplay]);

  const scrollToAxe = useCallback(
    (index: number) => {
      const list = cardListRef.current;
      const scrollContainer = getScrollParent(list);
      if (!list || !scrollContainer) return;
      const anchor =
        list.querySelectorAll<HTMLElement>("[data-axe-anchor]")[index];
      if (!anchor) return;
      const stickyTop = stickyOffsets[index] ?? 0;
      const anchorTop = anchor.getBoundingClientRect().top;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const target =
        scrollContainer.scrollTop + (anchorTop - containerTop) - stickyTop;
      scrollContainer.scrollTo({ top: target, behavior: "smooth" });
    },
    [stickyOffsets]
  );

  return (
    <div className={styles.root}>
      <div className={styles.cardContainer}>
        <span className={styles.cardContainerHeader}>
          <h4 className={styles.cardContainerTitle}>Contrôle quinquennal</h4>
        </span>
        <div className={styles.cardList} ref={cardListRef}>
          {axesToDisplay?.map((axe, index) => {
            const { label } = AXE_LABELS[axe]!;
            const slug = getQuestionnaireSlug(axe);
            const subAxis = getSubAxisForAxe(assujetti.id, axe);
            const verification = subAxis
              ? verificationsBySubAxis?.get(subAxis)
              : undefined;
            return (
              <AxeCard
                key={`${axe}-${assujetti.id}`}
                axeId={axe}
                label={label}
                slug={slug}
                contextType="ASSUJETTI"
                contextId={assujetti.id}
                stickyTop={stickyOffsets[index] ?? 0}
                onOpen={() => scrollToAxe(index)}
                verification={verification}
                declaredIobspActivites={declaredIobspActivites}
                disabled={disabled}
                onRelaunchVerification={
                  subAxis && onRelaunchSubAxis
                    ? () => onRelaunchSubAxis(subAxis)
                    : undefined
                }
                isRelaunchingVerification={
                  !!subAxis && relaunchingSubAxis === subAxis
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
